import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

const DEFAULT_PAGE = 1;
const EMPTY_OBJECT = {};

const getPositiveNumberParam = (searchParams, key, fallback) => {
    const value = Number(searchParams.get(key));
    return Number.isFinite(value) && value > 0 ? value : fallback;
};

const normalizeFilterValue = (value) => {
    if (Array.isArray(value)) {
        return value[0];
    }

    return value;
};

const parseOrdering = (ordering) => {
    if (!ordering) {
        return {sortField: '', sortOrder: '', ordering: ''};
    }

    if (ordering.startsWith('-')) {
        return {
            sortField: ordering.slice(1),
            sortOrder: 'descend',
            ordering,
        };
    }

    return {
        sortField: ordering,
        sortOrder: 'ascend',
        ordering,
    };
};

const getColumnId = (column) => column.key ?? column.dataIndex;

const getSortField = (column) => column?.orderIndex || column?.dataIndex || column?.key;

const buildFilterQueryMap = (filterParamMap) => Object.entries(filterParamMap).reduce((result, [tableKey, queryKey]) => {
    result[queryKey] = tableKey;
    return result;
}, {});

const getValueParser = (valueParsers, key) => valueParsers[key] || ((value) => value);

function useUrlTableState({
    defaultPageSize = 20,
    filterParamMap = EMPTY_OBJECT,
    filterValueParsers = EMPTY_OBJECT,
    filterLabels = EMPTY_OBJECT,
    searchText = '',
    includePagination = true,
} = {}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const filterQueryMap = useMemo(() => buildFilterQueryMap(filterParamMap), [filterParamMap]);
    const currentPage = getPositiveNumberParam(searchParams, 'page', DEFAULT_PAGE);
    const pageSize = getPositiveNumberParam(searchParams, 'page_size', defaultPageSize);
    const querySearchText = searchParams.get('search') ?? searchText;
    const {sortField, sortOrder, ordering} = parseOrdering(searchParams.get('ordering') || '');

    const filters = useMemo(() => (
        Object.entries(filterQueryMap).reduce((params, [queryKey]) => {
            const value = searchParams.get(queryKey);

            if (value !== null && value !== '') {
                params[queryKey] = getValueParser(filterValueParsers, queryKey)(value);
            }

            return params;
        }, {})
    ), [filterQueryMap, filterValueParsers, searchParams]);

    const tableFilters = useMemo(() => (
        Object.entries(filterParamMap).reduce((params, [tableKey, queryKey]) => {
            const value = searchParams.get(queryKey);

            if (value !== null && value !== '') {
                params[tableKey] = [getValueParser(filterValueParsers, queryKey)(value)];
            }

            return params;
        }, {})
    ), [filterParamMap, filterValueParsers, searchParams]);

    const controlledColumns = useCallback((columns) => columns.map((column) => {
        const columnId = getColumnId(column);
        const columnSortField = getSortField(column);

        return {
            ...column,
            filteredValue: tableFilters[columnId] ?? null,
            sortOrder: sortField && columnSortField === sortField ? sortOrder : null,
        };
    }), [sortField, sortOrder, tableFilters]);

    const updateSearchParams = useCallback((updates = {}, resetKeys = []) => {
        const nextParams = new URLSearchParams(searchParams);

        resetKeys.forEach((key) => nextParams.delete(key));

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                nextParams.delete(key);
            } else {
                nextParams.set(key, String(value));
            }
        });

        setSearchParams(nextParams, {replace: true});
    }, [searchParams, setSearchParams]);

    const resetState = useCallback(() => {
        const nextParams = new URLSearchParams(searchParams);
        const keysToReset = [
            'search',
            'page',
            'page_size',
            'ordering',
            ...Object.values(filterParamMap),
        ];

        keysToReset.forEach((key) => nextParams.delete(key));
        setSearchParams(nextParams, {replace: true});
    }, [filterParamMap, includePagination, searchParams, setSearchParams]);

    const removeState = useCallback((key) => {
        const nextParams = new URLSearchParams(searchParams);

        nextParams.delete(key);

        if (key !== 'page' && key !== 'page_size' && includePagination) {
            nextParams.set('page', '1');
        }

        setSearchParams(nextParams, {replace: true});
    }, [includePagination, searchParams, setSearchParams]);

    const handleTableChange = useCallback((columns) => (pagination, tableFilterValues, sorter, extra = {}) => {
        if (extra.action === 'paginate') {
            updateSearchParams({
                page: pagination.current,
                page_size: pagination.pageSize,
            });
            return;
        }

        const sorterField = sorter?.field || sorter?.columnKey;
        const column = columns.find((item) => getColumnId(item) === sorterField || item.key === sorterField);
        const orderField = getSortField(column) || sorterField;
        const filterQueryKeys = Object.values(filterParamMap);
        const updates = {
            ...(includePagination ? {page: 1} : {}),
            ordering: sorter?.order && orderField
                ? `${sorter.order === 'descend' ? '-' : ''}${orderField}`
                : null,
        };

        Object.entries(tableFilterValues).forEach(([tableKey, value]) => {
            const queryKey = filterParamMap[tableKey] || tableKey;
            const normalizedValue = normalizeFilterValue(value);
            updates[queryKey] = normalizedValue;
        });

        updateSearchParams(updates, filterQueryKeys);
    }, [filterParamMap, includePagination, updateSearchParams]);

    const stateIndicators = useMemo(() => {
        const indicators = [];

        if (querySearchText) {
            indicators.push({key: 'search', label: 'Search', value: querySearchText});
        }

        Object.entries(filters).forEach(([key, value]) => {
            indicators.push({
                key,
                label: filterLabels[key] || key,
                value,
            });
        });

        if (ordering) {
            const isDescending = ordering.startsWith('-');
            indicators.push({
                key: 'ordering',
                label: 'Sort',
                value: `${ordering.replace('-', '')} ${isDescending ? 'DESC' : 'ASC'}`,
            });
        }

        return indicators;
    }, [filterLabels, filters, ordering, querySearchText]);

    return {
        currentPage,
        pageSize,
        search: querySearchText,
        ordering,
        sortField,
        sortOrder,
        filters,
        tableFilters,
        stateIndicators,
        hasActiveState: stateIndicators.length > 0,
        controlledColumns,
        handleTableChange,
        updateSearchParams,
        removeState,
        resetState,
    };
}

export default useUrlTableState;
